import { useIsSoftCreditCheckApplication } from "@faire/deprecated-retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useIsSoftCreditCheckApplication";
import { Core } from "@faire/design-tokens";
import { trackOpenWithFaireOwfSoftCreditCheckLearnMoreCtaClickClick } from "@faire/web-api--source/events/openWithFaire/click/owfSoftCreditCheckLearnMoreCtaClick";
import { trackOpenWithFaireOwfSoftCreditCheckModalViewView } from "@faire/web-api--source/events/openWithFaire/view/owfSoftCreditCheckModalView";
import { trackEvent } from "@faire/web/common/trackEvent";
import { Checkbox } from "@faire/web/slate-deprecated/Checkbox";
import { Button } from "@faire/web/slate/Button";
import { Column, HorizontalLine, Row } from "@faire/web/slate/Layout";
import { Modal } from "@faire/web/slate/Modal";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Span, Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { Color as ColorSpan } from "@faire/web/ui/Typography/Span";
import React, { FC, useState } from "react";
import styled from "styled-components";

interface IProps {
  checked: boolean;
  error?: string;
  onChange: (checked: boolean) => void;
  eventPathBase?: string;
  isOpenWithFaire: boolean;
  isOwFApplicationForm?: boolean;
}

const CreditCheckHeading: React.FC = () => {
  const { isSoftCreditCheckApplication } = useIsSoftCreditCheckApplication();
  if (isSoftCreditCheckApplication) {
    return (
      <Typography>I agree to a soft check on my credit history</Typography>
    );
  }

  return (
    <Typography>
      I authorize Faire to perform a soft check on my credit as part of this
      application.
      <ColorSpan color={Core.icon.critical}>*</ColorSpan>
    </Typography>
  );
};

const CreditCheckBody: React.FC<{
  toggleModal: () => void;
}> = ({ toggleModal }) => {
  const { isSoftCreditCheckApplication } = useIsSoftCreditCheckApplication();
  if (isSoftCreditCheckApplication) {
    return (
      <Span color={Core.text.subdued}>
        Soft checks help us determine your new limit and do not affect your
        credit score.&nbsp;
        <StyledButton
          onClick={() => {
            toggleModal();
            trackOpenWithFaireOwfSoftCreditCheckLearnMoreCtaClickClick();
          }}
          variant="tertiary"
        >
          Learn More
        </StyledButton>
      </Span>
    );
  }

  return (
    <Typography color={Core.text.subdued}>
      A soft credit check helps us determine your net 60 terms limit and does
      NOT affect your credit score.{" "}
      <Button onClick={toggleModal} variant="tertiary">
        Learn More
      </Button>
    </Typography>
  );
};

const CreditCheckModalBody: React.FC<{
  toggleModal: () => void;
  isOpenWithFaire: boolean;
}> = ({ toggleModal, isOpenWithFaire }) => {
  const { isSoftCreditCheckApplication } = useIsSoftCreditCheckApplication();
  if (isOpenWithFaire) {
    if (isSoftCreditCheckApplication) {
      return (
        <>
          <Typography align="left" variant="pageHeaderSerifRegular">
            About soft credit checks
          </Typography>
          <Spacer height="4x" />
          <Typography align="left">
            When you apply, we’ll determine if you’re eligible for more payment
            terms based on the information you provide and a soft check of your
            credit history. Don’t worry, this check won’t impact your credit
            score at all.
          </Typography>
          <br />
          <Typography align="left">
            Please note that Faire isn’t a Credit Repair Organization as defined
            under federal or state law. We don’t offer advice, assistance, or
            services to help rebuild or improve your credit record, credit
            history, or credit rating. The FTC website is a great resource if
            you’re looking for information on managing your credit.
          </Typography>
        </>
      );
    }

    return (
      <>
        <Typography align="center">
          We determine your eligibility for Open with Faire using the
          information you provide in your application, along with your credit
          report obtained through a soft credit check. A soft credit check will
          not affect your credit score in any way.
        </Typography>
        <Spacer height="3x" />
        <HorizontalLine width="56px" />
        <Spacer height="3x" />
        <Typography align="center">
          Faire is not a Credit Repair Organization as defined under federal or
          state law, including the Credit Repair Organizations Act. Faire does
          not provide “credit repair” services or advice or assistance regarding
          “rebuilding” or “improving” your credit record, credit history, or
          credit rating. For more detail, please visit the FTC website.
        </Typography>
        <Spacer height="5x" />
        <Button onClick={toggleModal} style={{ maxWidth: "388px" }}>
          Close
        </Button>
      </>
    );
  }

  return (
    <>
      <Typography align="center">
        We determine your eligibility for a higher increase using the
        information you provide in your application, along with your credit
        report obtained through a soft credit check. A soft credit check will
        not affect your credit score in any way.
      </Typography>
      <Spacer height="3x" />
      <HorizontalLine width="56px" />
      <Spacer height="3x" />
      <Typography align="center">
        Faire is not a Credit Repair Organization as defined under federal or
        state law, including the Credit Repair Organizations Act. Faire does not
        provide “credit repair” services or advice or assistance regarding
        “rebuilding” or “improving” your credit record, credit history, or
        credit rating. For more detail, please visit the FTC website.
      </Typography>
      <Spacer height="5x" />
      <Button onClick={toggleModal} style={{ maxWidth: "388px" }}>
        Close
      </Button>
    </>
  );
};

const CreditCheckAuthorizationContent: React.FC<{
  error?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  toggleModal: () => void;
}> = ({ error, checked, onChange, toggleModal }) => {
  const { isSoftCreditCheckApplication } = useIsSoftCreditCheckApplication();
  const borderColor =
    isSoftCreditCheckApplication && !!error
      ? `${Core.border.critical}`
      : undefined;

  return (
    <>
      <Checkbox
        checked={checked}
        align="flex-start"
        onChange={() => onChange(!checked)}
        data-test-id="owf-credit-auth"
        borderColor={borderColor}
      >
        {isSoftCreditCheckApplication ? <Spacer width="1x" /> : null}
        <CreditCheckHeading />
      </Checkbox>
      <Row>
        {isSoftCreditCheckApplication ? (
          <Spacer width="4x" />
        ) : (
          <Spacer width="3x" />
        )}
        <CreditCheckBody toggleModal={toggleModal} />
      </Row>
    </>
  );
};

export const CreditCheckAuthorizationSection: FC<IProps> = ({
  checked,
  error,
  onChange,
  eventPathBase,
  isOpenWithFaire,
}) => {
  const { isSoftCreditCheckApplication } = useIsSoftCreditCheckApplication();
  const [showModal, setShowModal] = useState(false);
  const toggleModal = () => {
    if (!showModal) {
      trackEvent(`${eventPathBase}/credit-check-modal/open`);
    }
    setShowModal((prevShowModal) => !prevShowModal);
  };

  const ref = useDoActionWhenVisible(
    trackOpenWithFaireOwfSoftCreditCheckModalViewView
  );

  const modalWidth = !isSoftCreditCheckApplication ? "691px" : "383px";
  const modalHeight = !isSoftCreditCheckApplication ? "474px" : "464px";

  return (
    <>
      <Column>
        {isSoftCreditCheckApplication ? (
          <>
            <CreditCheckAuthorizationContent
              error={error}
              checked={checked}
              onChange={onChange}
              toggleModal={toggleModal}
            />
            <Spacer height="1x" />
            {error ? (
              <Row>
                <Spacer width="4x" />
                <Typography
                  variant="labelSansRegular"
                  color={Core.text.critical}
                >
                  {error}
                </Typography>
              </Row>
            ) : null}
            <Spacer width="1x" />
          </>
        ) : (
          <>
            <Box $hasError={!!error}>
              <CreditCheckAuthorizationContent
                checked={checked}
                onChange={onChange}
                toggleModal={toggleModal}
              />
            </Box>
            {error ? (
              <Typography color={Core.text.critical}>{error}</Typography>
            ) : null}
          </>
        )}
      </Column>
      {showModal ? (
        <Modal
          onClose={toggleModal}
          width={modalWidth}
          height={modalHeight}
          padding="0"
        >
          {isSoftCreditCheckApplication ? (
            <SoftCreditCheckModalWrapper ref={ref}>
              <CreditCheckModalBody
                toggleModal={toggleModal}
                isOpenWithFaire={isOpenWithFaire}
              />
            </SoftCreditCheckModalWrapper>
          ) : (
            <ModalWrapper>
              <CreditCheckModalBody
                toggleModal={toggleModal}
                isOpenWithFaire={isOpenWithFaire}
              />
            </ModalWrapper>
          )}
        </Modal>
      ) : null}
    </>
  );
};

const Box = styled.div<{ $hasError: boolean }>`
  padding: 16px;
  border: 1px solid
    ${({ $hasError }) =>
      $hasError ? Core.icon.critical : Core.surface.subdued};
`;

const ModalWrapper = styled(Column).attrs({
  justify: "center",
  align: "center",
})`
  width: 100%;
  flex: 1;
  padding: ${getSpacing(0, "2x")};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing(0, "9x")};
  }
`;

const SoftCreditCheckModalWrapper = styled(Column).attrs({
  justify: "center",
})`
  width: 100%;
  flex: 1;
  padding: ${getSpacing(0, "4x")};
`;

const StyledButton = styled(Button)`
  color: ${Core.text.subdued};
`;
